import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { getEntitlementEvaluations } from '../api/entitlementsApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function formatDate(value, emptyValue) {
  if (!value) {
    return emptyValue;
  }

  return new Date(value).toLocaleString();
}

export function EntitlementEvaluationsPage() {
  const { t } = useTranslation(['features', 'navigation', 'common']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');
  const recordCount = rows.length;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getEntitlementEvaluations({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(() => [
    {
      id: 'feature_code',
      label: t('features:evaluations.columns.feature'),
      render: (row) => (
        row.feature_code
          ? <Typography sx={monoSx}>{row.feature_code}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'enabled',
      label: t('features:evaluations.columns.enabled'),
      render: (row) => (
        <StatusBadge
          status={row.enabled ? 'yes' : 'no'}
          category="generic"
          apiLabel={row.enabled ? t('common:yes') : t('common:no')}
          size="Sm"
          tone={row.enabled ? 'Success' : 'Neutral'}
        />
      ),
    },
    {
      id: 'source',
      label: t('features:evaluations.columns.source'),
      render: (row) => (
        row.source
          ? (
            <StatusBadge
              status={row.source}
              category="generic"
              apiLabel={row.source}
              size="Sm"
              tone="Neutral"
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'tenant_id',
      label: t('features:evaluations.columns.tenant'),
      render: (row) => (
        row.tenant_id != null && row.tenant_id !== ''
          ? <Typography sx={monoSx}>{row.tenant_id}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'customer_id',
      label: t('features:evaluations.columns.customer'),
      render: (row) => (
        row.customer_id != null && row.customer_id !== ''
          ? <Typography sx={monoSx}>{row.customer_id}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'evaluated_at',
      label: t('features:evaluations.columns.evaluated'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            lineHeight: '20px',
            color: semantic.text.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(row.evaluated_at, emptyValue)}
        </Typography>
      ),
    },
  ], [emptyValue, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('features:evaluations.title')}
        subtitle={t('features:evaluations.subtitle')}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={load} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('features:evaluations.load_failed')}
        </Alert>
      )}

      {!error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('features:evaluations.internalNotice')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('features:evaluations.title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('features:evaluations.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : !error ? (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            emptyMessage={t('features:evaluations.no_evaluations')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        ) : null}
      </SectionCard>
    </Box>
  );
}
